import { useState } from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Radio, Users, Clock, CalendarDays, FileSpreadsheet, Navigation, MapPin, Megaphone, Plane, Wallet, BarChart3, FolderOpen, CreditCard, Settings, Menu, ChevronsUpDown, LogOut, UserCog, MailWarning, Home, UserCircle, Receipt } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/app/NotificationBell";

export const FULL_ONLY = ["time-clock", "timesheets"];

export const NAV = [
  [LayoutDashboard, "Dashboard", "", "employee"], [Radio, "Live Attendance", "live-attendance", "manager"], [Users, "Employees", "employees", "manager"],
  [Clock, "Time Clock", "time-clock", "employee"], [CalendarDays, "Staff Rosters", "rosters", "employee"], [FileSpreadsheet, "Timesheets", "timesheets", "employee"],
  [Navigation, "GPS Tracking", "gps-tracking", "manager"], [MapPin, "Geofences", "geofences", "manager"], [Megaphone, "Announcements", "announcements", "employee"],
  [Plane, "Leave Requests", "leave", "employee"], [Wallet, "Payroll", "payroll", "payroll"], [BarChart3, "Reports", "reports", "manager"], [Receipt, "Expenses", "expenses", "manager"],
  [FolderOpen, "Documents", "documents", "employee"], [CreditCard, "Subscription", "subscription", "owner"], [Settings, "Settings", "settings", "owner"],
];
const RANK = { employee: 1, manager: 2, owner: 3 };

const BusinessSwitcher = () => {
  const { memberships, membership, switchBusiness } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left" data-testid="business-switcher">
        <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Business · {membership?.role}</p><p className="truncate text-sm font-semibold" data-testid="current-business-name">{membership?.business_name}</p></div>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel>Switch business</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem key={m.business_id} onClick={() => { switchBusiness(m.business_id); navigate("/app"); }} data-testid={`switch-business-${m.business_id}`}>
            <span className="truncate">{m.business_name}</span><span className="ml-auto text-xs capitalize text-slate-400">{m.role}</span>
          </DropdownMenuItem>
        ))}
        {membership?.role === "owner" && <DropdownMenuItem onClick={() => navigate("/app/onboarding?new=1")} className="font-semibold text-[#00A85C]" data-testid="add-business-btn">+ Add another business</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SideNav = ({ onNavigate }) => {
  const { membership } = useAuth();
  const items = NAV.filter((n) => (n[3] === "payroll" ? membership?.payroll_access : RANK[membership?.role] >= RANK[n[3]]))
    .filter((n) => !(membership?.role === "employee" && membership?.app_access === "leave_only" && FULL_ONLY.includes(n[2])));
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo size="sm" to="/app" /></div>
      <div className="mx-4 mb-3"><BusinessSwitcher /></div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">
        {items.map(([Icon, label, path]) => (
          <NavLink key={label} to={`/app${path ? `/${path}` : ""}`} end={!path} onClick={onNavigate} data-testid={`app-nav-${path || "dashboard"}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-emerald-50 text-[#00A85C]" : "text-slate-600 hover:bg-slate-50"}`}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

const BottomNav = () => {
  const { membership } = useAuth();
  const full = membership?.app_access !== "leave_only";
  const items = [[Home, "Home", ""], ...(full ? [[Clock, "Time Clock", "time-clock"], [FileSpreadsheet, "My Hours", "timesheets"]] : [[CalendarDays, "Roster", "rosters"], [Plane, "Leave", "leave"]]), [Megaphone, "News", "announcements"], [UserCircle, "Profile", "profile"]];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E9EEF2] bg-white pb-[env(safe-area-inset-bottom)] lg:hidden" data-testid="bottom-nav">
      <div className="mx-auto flex max-w-xl">
        {items.map(([Icon, label, path]) => (
          <NavLink key={label} to={`/app${path ? `/${path}` : ""}`} end={!path} data-testid={`bottom-nav-${path || "home"}`}
            className={({ isActive }) => `flex min-w-0 flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold transition-colors ${isActive ? "text-[#00A85C]" : "text-slate-500"}`}>
            {({ isActive }) => <><span className={`grid h-8 w-12 place-items-center rounded-full ${isActive ? "bg-[#00C16B]/15" : ""}`}><Icon className="h-5 w-5" /></span><span className="truncate">{label}</span></>}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3" data-testid="user-menu-btn">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[#0B2D5B] text-xs font-bold text-amber-300">{user.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>
        <span className="hidden text-sm font-medium sm:block">{user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="truncate text-xs font-normal text-slate-500">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/app/account")} data-testid="user-menu-account"><UserCog className="mr-2 h-4 w-4" />Account & security</DropdownMenuItem>
        {user.is_super_admin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin"><UserCog className="mr-2 h-4 w-4" />Platform admin</DropdownMenuItem>}
        <DropdownMenuItem onClick={async () => { navigate("/login", { replace: true }); await logout(); }} data-testid="user-menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const BillingBanner = () => {
  const { business, membership } = useAuth();
  const sub = business?.subscription || {};
  const access = business?.access || {};
  const end = sub.trial_ends_at ? new Date(sub.trial_ends_at) : null;
  const days = end ? Math.ceil((end - Date.now()) / 86400000) : null;
  const until = access.grace_ends_at ? new Date(access.grace_ends_at).toLocaleDateString("en-AU", { day: "numeric", month: "long" }) : "";
  let msg = null;
  const failed = access.reason === "payment_failed";
  if (access.state === "read_only") msg = failed ? "Clockaroo is read-only because your subscription payment failed. Your data is safe." : "Clockaroo is read-only because there's no active plan. Your data is safe.";
  else if (access.state === "grace") msg = access.reason === "payment_failed" ? `Your last subscription payment failed. Update your card by ${until} to avoid read-only mode.` : access.reason === "comp_ended" ? `Your complimentary access has ended. Choose a plan by ${until} before Clockaroo becomes read-only.` : `Your free trial has ended. Choose a plan by ${until} before Clockaroo becomes read-only.`;
  else if (access.state === "trial" && !sub.billing_active && days !== null && days <= 3) msg = `Your free trial ends in ${days} day${days === 1 ? "" : "s"}.`;
  if (!msg) return null;
  return <div className="flex flex-wrap items-center gap-2 bg-orange-50 px-6 py-2 text-sm text-orange-900" data-testid="billing-banner">{msg}{membership?.role === "owner" ? <Link to="/app/subscription" className="font-semibold underline" data-testid="billing-banner-link">{failed ? "Update card" : "Choose a plan"}</Link> : " Please let the business owner know."}</div>;
};

export default function AppLayout() {
  const { user, memberships, membership } = useAuth();
  const [open, setOpen] = useState(false);
  if (!memberships.length) return <Navigate to={user?.is_super_admin ? "/admin" : "/app/onboarding"} replace />;
  if (!membership) return null;
  if (membership.business_status === "suspended") {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-6" data-testid="business-suspended">
        <div className="max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center">
          <p className="text-lg font-bold">This business account is suspended</p>
          <p className="mt-2 text-sm text-slate-600">{membership.business_name} can't use Clockaroo right now. Your records are safe. Please contact Clockaroo to sort this out.</p>
          <div className="mt-5 flex justify-center gap-2"><Link to="/contact" className="rounded-full bg-[#00C16B] px-5 py-2 text-sm font-semibold text-white" data-testid="suspended-contact-link">Contact Clockaroo</Link><UserMenu /></div>
        </div>
      </div>
    );
  }
  if (membership.approval === "pending" && membership.role === "employee") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#E9EEF2] p-6" data-testid="pending-approval">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <Logo size="sm" to="/app" />
          <p className="mt-5 font-display text-lg font-bold text-[#0B2D5B]">Almost there!</p>
          <p className="mt-2 text-sm text-slate-600" data-testid="pending-approval-msg">Your employer is setting up your account. You will be notified when your account is approved.</p>
          <div className="mt-5 flex justify-center gap-2"><NotificationBell /><UserMenu /></div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen overflow-x-hidden bg-slate-50" data-testid="app-layout">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-slate-200 bg-white lg:block"><SideNav /></aside>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent side="left" className="w-72 p-0"><SheetTitle className="sr-only">Navigation</SheetTitle><SideNav onNavigate={() => setOpen(false)} /></SheetContent></Sheet>
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open navigation" data-testid="app-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          <div className="flex-1" />
          <NotificationBell />
          <UserMenu />
        </header>
        {!user.email_verified && (
          <div className="flex items-center gap-2 bg-amber-50 px-6 py-2 text-sm text-amber-900" data-testid="verify-email-banner"><MailWarning className="h-4 w-4" />Please verify your email address. <Link to="/app/onboarding?step=account" className="font-semibold underline">Resend</Link></div>
        )}
        <BillingBanner />
        <main className={`min-w-0 p-4 sm:p-6 lg:p-8 ${membership.role === "employee" ? "pb-28 lg:pb-8" : ""}`}><Outlet /></main>
      </div>
      {membership.role === "employee" && <BottomNav />}
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>{eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-[#E07800]">{eyebrow}</p>}<h1 className="text-2xl font-bold tracking-tight sm:text-3xl" data-testid="page-title">{title}</h1></div>
    {children}
  </div>
);
